import z from "zod";

import { CompanyType } from "./IUser.type";

export enum SupportedLanguage {
  EN = "en",
  DE = "de",
}

export const isPhoneNumberValid = (phoneNumber: string): boolean =>
  /^\+[1-9]\d{6,14}$/.test(phoneNumber.replace(/\s/g, ""));

export const isPhoneNumberSupported = (phoneNumber: string) =>
  !phoneNumber.match(/^\+380/);

export const createUserSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  mobileNumber: z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      if (!val) return;

      if (!isPhoneNumberValid(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Please include country code (e.g., +49) and do not use dashes or spaces. Example: +4915112345678",
        });
      }
      if (!isPhoneNumberSupported(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Phone number not supported",
        });
      }
    }),
  language: z
    .nativeEnum(SupportedLanguage, {
      errorMap: () => ({ message: "Please select a valid language" }),
    })
    .optional()
    .default(SupportedLanguage.DE),
  street: z.string().optional(),
  postCode: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{5}$/.test(val), {
      message: "Post code must be 5 digits",
    }),
  city: z.string().optional(),
  deTaxId: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{11}$/.test(val), {
      message: "Tax ID must be 11 digits",
    }),
  birthDate: z.coerce
    .date()
    .refine((val) => val < new Date(), {
      message: "Birth date must be in the past",
    })
    .optional(),
  businessTradingName: z.string().optional(),
  preferredName: z.string().optional(),
  companyType: z.nativeEnum(CompanyType).optional(),
});

export const createKontaxUserSchema = z.object({
  businessType: z.string().optional(),
});

export const createUserRequestDto = z.object({
  user: createUserSchema,
  kontaxUser: createKontaxUserSchema,
});

export type CreateUserRequestDto = z.infer<typeof createUserRequestDto>;
